import {
  Typography, Box, Grid,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button,
  Switch
} from "@mui/material";
import React, { useState } from "react";
import style from "./style";
import Modal from "../../components/Modal";
import { formatDate } from "../../utils/DateFormat";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ReactComponent as Edit } from '../../assets/images/edit.svg';
import dayjs from "dayjs";

const FinancialYearPage = ({
  country,
  financialYears,
  loading,
  handleAddFinancialYear,
  handleUpdateFinancialYear,
  handleDeleteFinancialYear,
  handleToggleStatus
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    start: null,
    end: null,
    isActive: true
  });
  const role = localStorage.getItem("role");
  const [originalData, setOriginalData] = useState({
    start: null,
    end: null,
    isActive: true
  });

  const countryPermissions = JSON.parse(
    localStorage.getItem("countryPermissions") || "{}"
  );

  const permissions = Object.values(countryPermissions)[0] || {};

  const canEditFinancialYear =
    role === "SuperAdmin" ||
    permissions?.financialYear?.edit === true;

  const canViewFinancialYear =
    permissions?.financialYear?.view === true;

  const handleOpenAddModal = () => {
    setEditMode(false);
    setFormData({
      start: null,
      end: null,
      isActive: true
    });
    setOpenModal(true);
  };

  const handleOpenEditModal = (item) => {
    setEditMode(true);
    setSelectedItem(item);
    const editFormData = {
      start: item.start ? dayjs(item.start) : null,
      end: item.end ? dayjs(item.end) : null,
      isActive: item.isActive ?? true
    };
    setFormData(editFormData);
    setOriginalData(editFormData);
    setOpenModal(true);
  };

  // Check if form is valid (both dates required and start < end)
  const isFormValid = () => {
    if (!formData.start || !formData.end) return false;
    // Start date must be before end date
    return dayjs(formData.start).isBefore(dayjs(formData.end));
  };

  // Check if form data has changed from original
  const hasChanges = () => {
    if (!editMode) return true; // For add mode, always enable

    const startChanged = formData.start?.toISOString() !== originalData.start?.toISOString();
    const endChanged = formData.end?.toISOString() !== originalData.end?.toISOString();

    return startChanged || endChanged;
  };

  // Check if button should be disabled
  const isButtonDisabled = () => {
    if (!isFormValid()) return true; // Disable if dates not filled
    if (editMode && !hasChanges()) return true; // Disable if no changes in edit mode
    return false;
  };

  const handleOpenDeleteModal = (item) => {
    setSelectedItem(item);
    setOpenDeleteModal(true);
  };

  const handleSubmit = () => {
    const submitData = {
      start: formData.start ? formData.start.toISOString() : null,
      end: formData.end ? formData.end.toISOString() : null,
      isActive: editMode && selectedItem ? selectedItem.isActive : true // Use existing status for edit, true for new
    };

    if (editMode && selectedItem) {
      handleUpdateFinancialYear(selectedItem._id, submitData);
    } else {
      handleAddFinancialYear(submitData);
    }
    setOpenModal(false);
    resetForm();
  };

  const handleDelete = () => {
    if (selectedItem) {
      handleDeleteFinancialYear(selectedItem._id);
    }
    setOpenDeleteModal(false);
    setSelectedItem(null);
  };

  const resetForm = () => {
    setFormData({
      start: null,
      end: null,
      isActive: true
    });
    setOriginalData({
      start: null,
      end: null,
      isActive: true
    });
    setSelectedItem(null);
    setEditMode(false);
  };

  // Generate FY name from start and end dates (e.g., "2024/2025")
  const getFYName = (start, end) => {
    if (!start || !end) return "-";
    const startYear = dayjs(start).format("YYYY");
    const endYear = dayjs(end).format("YYYY");
    return `${startYear}/${endYear}`;
  };

  return (
    <>
      <Box sx={style.headerContainer}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography sx={style.gridText}>Financial Year</Typography>
        </Box>
        {canEditFinancialYear && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAddModal}
            sx={style.addButton}
            disabled={!country}
          >
            + Add Financial Year
          </Button>
        )}
      </Box>

      <Grid container sx={style.mainContainer}>
        <Grid item xs={12} lg={12} sx={style.gridbox}>
          <TableContainer sx={style.noShadow}>
            <Table >
              <TableHead>
                <TableRow>
                  <TableCell sx={style.tableHead}>FY</TableCell>
                  <TableCell sx={style.tableHead}>Start Date</TableCell>
                  <TableCell sx={style.tableHead}>End Date</TableCell>

                  {canEditFinancialYear && (
                    <TableCell sx={style.tableHead}>Status</TableCell>
                  )}

                  {canEditFinancialYear && (
                    <TableCell sx={style.tableHead}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {financialYears && financialYears.length > 0 ? (
                  financialYears.map((item, index) => (
                    <TableRow key={item._id || index}>
                      <TableCell sx={style.tabledata}>{getFYName(item.start, item.end)}</TableCell>
                      <TableCell sx={style.tabledata}>{formatDate(item.start)}</TableCell>
                      <TableCell sx={style.tabledata}>{formatDate(item.end)}</TableCell>
                      {canEditFinancialYear && (
                        <TableCell sx={style.tabledata}>
                          <Switch
                            checked={item.isActive}
                            onChange={() =>
                              handleToggleStatus(item._id, !item.isActive, item)
                            }
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                      )}
                      {canEditFinancialYear && (
                        <TableCell sx={style.tabledata}>
                          <Box sx={style.tableActiondata}>
                            <Edit
                              style={{
                                fontSize: "22px",
                                color: "#878787",
                                cursor: "pointer"
                              }}
                              onClick={() => handleOpenEditModal(item)}
                            />

                            <DeleteOutlinedIcon
                              sx={{
                                fontSize: "22px",
                                color: item.isActive ? "#ccc" : "#ff0000",
                                cursor: item.isActive ? "not-allowed" : "pointer",
                                opacity: item.isActive ? 0.5 : 1
                              }}
                              onClick={() =>
                                !item.isActive && handleOpenDeleteModal(item)
                              }
                            />
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={canEditFinancialYear ? 5 : 3}
                      sx={style.emptyState}
                    >
                      {loading
                        ? "Loading..."
                        : country
                          ? "No Financial Years Found"
                          : "Select a country to view Financial Years"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Add/Edit Modal */}
      <Modal
        open={openModal}
        setOpen={setOpenModal}
        btnText={editMode ? "Update" : "Add"}
        title={editMode ? "Edit Financial Year" : "Add Financial Year"}
        handleCheck={handleSubmit}
        disabled={isButtonDisabled()}
      >
        <Box sx={{ minWidth: 350 }}>
          <Box sx={style.inputBox}>
            <Typography sx={style.inputLabel}>Start Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={formData.start}
                onChange={(newValue) => setFormData({ ...formData, start: newValue })}
                slotProps={{
                  textField: {
                    sx: style.dateInput,
                    fullWidth: true,
                    placeholder: "Select Start Date"
                  }
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={style.inputBox}>
            <Typography sx={style.inputLabel}>End Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={formData.end}
                onChange={(newValue) => setFormData({ ...formData, end: newValue })}
                slotProps={{
                  textField: {
                    sx: style.dateInput,
                    fullWidth: true,
                    placeholder: "Select End Date"
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        deletetText="Delete"
        cancelText="Cancel"
        title="Delete Financial Year"
        handleDelete={handleDelete}
      >
        <Box>
          <Typography>Are you sure you want to delete this Financial Year?</Typography>
          {selectedItem && (
            <Typography sx={{ fontWeight: 600, mt: 1 }}>
              {getFYName(selectedItem.start, selectedItem.end)}
            </Typography>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default FinancialYearPage;
